import { X, ExternalLink, Calendar, MapPin, Award } from 'lucide-react';

export const CardDetailModal = ({ isOpen, onClose, card, type }) => {
  if (!isOpen || !card) return null;

  const renderExperienceDetails = () => (
    <div className="space-y-4">
      <div className="flex items-center space-x-4">
        <div className="text-3xl">{card.logo || card.details?.logo || '🏢'}</div>
        <div>
          <h2 className="text-2xl font-bold text-white">{card.title}</h2>
          <p className="text-lg text-netflix-red">{card.subtitle}</p>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="flex items-center space-x-2 text-netflix-gray-400 text-sm">
          <Calendar className="w-4 h-4" />
          <span>{card.period || card.subtitle?.split(' • ')[1] || 'Current'}</span>
        </div>
        <div className="flex items-center space-x-2 text-netflix-gray-400 text-sm">
          <MapPin className="w-4 h-4" />
          <span>{card.location || 'Remote'}</span>
        </div>
      </div>
      
      <div className="bg-netflix-gray-100 rounded p-3">
        <p className="text-white text-sm leading-relaxed">{card.description}</p>
      </div>
      
      {card.details?.achievements && (
        <div>
          <h3 className="text-lg font-semibold text-white mb-2 flex items-center">
            <Award className="w-4 h-4 mr-2 text-netflix-red" />
            Key Achievements
          </h3>
          <ul className="space-y-1 max-h-32 overflow-y-auto">
            {card.details.achievements.map((achievement, index) => (
              <li key={index} className="flex items-start space-x-2">
                <div className="w-1.5 h-1.5 bg-netflix-red rounded-full mt-2 flex-shrink-0" />
                <span className="text-netflix-gray-400 text-sm">{achievement}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {card.details?.technologies && (
        <div>
          <h3 className="text-lg font-semibold text-white mb-2">Technologies & Frameworks</h3>
          <div className="flex flex-wrap gap-1">
            {card.details.technologies.map((tech, index) => (
              <span key={index} className="netflix-badge netflix-badge-secondary text-xs">
                {tech}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderProjectDetails = () => {
    const getStrategicValue = (title) => {
      const strategicValues = {
        "FedRAMP 20x Automation": [
          "Government Cloud Market Leadership",
          "Compliance Cost Reduction & Efficiency",
          "Competitive Advantage in Federal Sector",
          "Regulatory Innovation & Standardization"
        ],
        "Gemini AI Safety Framework": [
          "AI Market Trust & Adoption",
          "Regulatory Compliance Future-Proofing",
          "Responsible AI Competitive Differentiation",
          "Global AI Safety Leadership"
        ],
        "Financial Controls Risk Transformation": [
          "Financial Risk Mitigation & Compliance",
          "Operational Efficiency & Cost Reduction",
          "Regulatory Examination Readiness",
          "Customer Trust & Market Confidence"
        ],
        "Shared Fate Security Model": [
          "Enterprise Customer Risk Assurance",
          "Systemic Risk Reduction & Resilience",
          "Competitive Cloud Security Advantage",
          "Regulatory Compliance at Scale"
        ],
        "LinkedIn Common Controls Program": [
          "Global Platform Security Standardization",
          "Operational Efficiency & Cost Optimization",
          "Regulatory Compliance Automation",
          "Enterprise Security Scalability"
        ],
        "Discover Cyber Insurance Optimization": [
          "Financial Risk Transfer & Protection",
          "Cost Optimization & Capital Efficiency",
          "Advanced Threat Coverage",
          "Risk Quantification Capability"
        ],
        "Red Flags Identity Theft Prevention": [
          "Customer Protection & Trust",
          "Regulatory Compliance Excellence",
          "Fraud Loss Reduction & ROI",
          "Competitive Security Advantage"
        ],
        "NYDFS Cybersecurity Compliance": [
          "Market Expansion Enablement",
          "Regulatory Excellence Demonstration",
          "Competitive Banking Advantage",
          "Zero-Risk Compliance Model"
        ]
      };
      
      return strategicValues[title] || [
        "Risk Mitigation & Compliance",
        "Operational Excellence & Efficiency",
        "Competitive Market Advantage",
        "Regulatory Leadership & Innovation"
      ];
    };

    const strategicValue = getStrategicValue(card.title);

    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-2xl font-bold text-white mb-1">{card.title}</h2>
          <p className="text-lg text-netflix-red mb-2">{card.subtitle}</p>
          {card.match && (
            <div className="text-green-400 font-semibold text-sm">{card.match}</div>
          )}
        </div>
        
        <div className="bg-netflix-gray-100 rounded p-3">
          <h3 className="text-base font-semibold text-white mb-2">Project Overview</h3>
          <p className="text-netflix-gray-400 text-sm leading-relaxed">{card.description}</p>
        </div>
        
        {card.impact && (
          <div className="bg-netflix-red/10 border border-netflix-red/30 rounded p-3">
            <h3 className="text-base font-semibold text-netflix-red mb-1">Business Impact</h3>
            <p className="text-white text-sm">{card.impact}</p>
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h3 className="text-base font-semibold text-white mb-2">Key Technologies</h3>
            <div className="flex flex-wrap gap-1">
              {(card.tags || []).map((tag, index) => (
                <span key={index} className="netflix-badge netflix-badge-secondary text-xs">
                  {tag}
                </span>
              ))}
            </div>
          </div>
          
          <div>
            <h3 className="text-base font-semibold text-white mb-2">Strategic Value</h3>
            <div className="space-y-1 text-netflix-gray-400 text-xs">
              {strategicValue.map((value, index) => (
                <div key={index}>• {value}</div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPublicationDetails = () => (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold text-white mb-1">{card.title}</h2>
        <p className="text-lg text-netflix-red mb-1">{card.subtitle}</p>
        {card.match && (
          <div className="text-green-400 font-semibold text-sm">{card.match}</div>
        )}
      </div>
      
      <div className="bg-netflix-gray-100 rounded p-3">
        <p className="text-white text-sm leading-relaxed">{card.description}</p>
      </div>
      
      {card.impact && (
        <div className="bg-netflix-red/10 border border-netflix-red/30 rounded p-3">
          <h3 className="text-base font-semibold text-netflix-red mb-1">Industry Impact</h3>
          <p className="text-white text-sm">{card.impact}</p>
        </div>
      )}
      
      <div className="flex flex-col space-y-3">
        {card.link && (
          <a 
            href={card.link} 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center netflix-btn-play text-sm w-fit"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            View Publication
          </a>
        )}
        <div className="flex flex-wrap gap-1">
          {(card.tags || []).map((tag, index) => (
            <span key={index} className="netflix-badge netflix-badge-secondary text-xs">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );

  const renderExpertiseDetails = () => (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold text-white mb-1">{card.title}</h2>
        <p className="text-lg text-netflix-red mb-2">{card.subtitle}</p>
      </div>
      
      <div className="bg-netflix-gray-100 rounded p-3">
        <h3 className="text-base font-semibold text-white mb-2">Domain Expertise</h3>
        <p className="text-netflix-gray-400 text-sm leading-relaxed">{card.description}</p>
      </div>
      
      {card.businessImpact && (
        <div className="bg-netflix-red/10 border border-netflix-red/30 rounded p-3">
          <h3 className="text-base font-semibold text-netflix-red mb-1">Typical Business Impact</h3>
          <p className="text-white text-sm">{card.businessImpact}</p>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h3 className="text-base font-semibold text-white mb-2">Key Capabilities</h3>
          <div className="space-y-1 text-netflix-gray-400 text-xs">
            {(card.keyCap || [
              "Strategic Risk Assessment & Quantification",
              "Regulatory Framework Implementation", 
              "Enterprise Security Architecture",
              "Board-Level Risk Communication",
              "Cross-Functional Program Leadership"
            ]).map((capability, index) => (
              <div key={index}>• {capability}</div>
            ))}
          </div>
        </div>
        
        <div>
          <h3 className="text-base font-semibold text-white mb-2">Industry Focus</h3>
          <div className="flex flex-wrap gap-1">
            {(card.tags || []).map((tag, index) => (
              <span key={index} className="netflix-badge netflix-badge-secondary text-xs">
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (type) {
      case 'experience':
        return renderExperienceDetails();
      case 'project':
        return renderProjectDetails();
      case 'publication':
        return renderPublicationDetails();
      case 'expertise':
        return renderExpertiseDetails();
      default:
        return renderProjectDetails();
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4">
      <div className="bg-netflix-black border border-netflix-gray-200 rounded-lg w-full max-w-2xl max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-netflix-gray-200">
          <h1 className="text-lg font-bold text-white">Details</h1>
          <button 
            onClick={onClose}
            className="text-netflix-gray-400 hover:text-white transition-colors p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(80vh-80px)]">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};